import queryKeys from "@configs/react-query/query-key";
import { useFetchData } from "@hooks/useFetchData";
import { useMutationData } from "@hooks/useMutationData";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import useSuccessToast from "@configs/react-toastify/snackbar";
import { TOAST } from "@constants/index";
import { NOTICE_STATUS } from "@utils/common";
import { useTranslation } from "react-i18next";

export const useNoticeInformation = (noticeInfoSeq) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [data, setData] = useState({
    noticeInfo: {},
    projectData: {},
    verificationReportData: {},
    licensePolicyData: {},
    scanComponents: [],
    noticeStatus: NOTICE_STATUS.REQUEST,
    noticeInfoComponents: [],
    noticeInfoStatusCdName: "Writing...",
    noticeInfoWriteStatusCd: NOTICE_STATUS.REQUEST,
  });

  // API
  const {
    data: fetchedNoticeInfoData,
    isLoading: isLoadingNoticeInfo,
    error: noticeInfoError,
    refetch: refetchNoticeInfoData
  } = useFetchData(
    [queryKeys.getNoticeInfo, noticeInfoSeq],
    `/notice_info/${noticeInfoSeq}`,
    { method: "GET" },
    {},
    { enabled: !!noticeInfoSeq },
  );

  useEffect(() => {
    if (noticeInfoError?.response?.status === 404) {
      navigate("/404", { state: { backTo: "/notice-information" } });
    }
  }, [noticeInfoError, navigate]);

  const {
    data: fetchedProjectByProjectID,
    isLoading: isLoadingProject,
    error: projectError,
  } = useFetchData(
    [queryKeys.getProjectById, data.noticeInfo?.projectId],
    `/project_by_project_id/${data.noticeInfo?.projectId}`,
    { method: "GET" },
    {},
    { enabled: !!data.noticeInfo?.projectId },
  );

  const {
    data: fetchedVerificationData,
    isLoading: isLoadingVerification,
    error: verificationError,
  } = useFetchData(
    [queryKeys.getVerificationReportBySeq, data.noticeInfo?.verificationReportSeq],
    `/verification_report/${data.noticeInfo?.verificationReportSeq}`,
    { method: "GET" },
    {},
    { enabled: !!data.noticeInfo?.verificationReportSeq },
  );

  const {
    data: fetchedLicensePolicy,
    isLoading: isLoadingLicensePolicy,
    error: licensePolicyError,
  } = useFetchData(
    [queryKeys.getLicensePolicyByLicensePolicySeq, data.projectData?.licensePolicySeq],
    `/license_policy/${data.projectData?.licensePolicySeq}`,
    { method: "GET" },
    {},
    { enabled: !!data.projectData?.licensePolicySeq },
  );


  const {
    data: fetchedNoticeInfoComponents,
    refetch: refetchNoticeInfoComponents
  } = useFetchData(
    [queryKeys.getdNoticeInfoComponentsByNoticeInfoSeq, noticeInfoSeq],
    `/notice_info_component_by_notice_info_seq/${noticeInfoSeq}`,
    { method: "GET" },
    {},
    { enabled: !!noticeInfoSeq },
  );


  const updateNoticeInfoNoticeInfoWriteStatus = useMutationData(
    queryKeys.updateNoticeInfoNoticeInfoWriteStatus,
    { method: "PUT" },
    {
      onSuccess: (response) => {
        useSuccessToast(TOAST.success, t("common.successfully-added"), t("common.write-completed"));
        setData((prev) => ({
          ...prev,
          noticeStatus: response.noticeInfoStatusCd,
          noticeInfoStatusCdName: response.noticeInfoStatusCdName,
          noticeInfoWriteStatusCd: response.noticeInfoWriteStatusCd,
        }));
        refetchNoticeInfoComponents();
        refetchNoticeInfoData();
      },
      onError: (error) => {
      },
    },
  );

  const updateNoticeInfoStatus = useMutationData(
    queryKeys.updateNoticeInfoStatus,
    { method: "PUT" },
    {
      onSuccess: (response) => {
        // Show success toast based on the new status
        const newStatus = response.noticeInfoStatusCd;
        if (newStatus === NOTICE_STATUS.ON_REVIEW) {
          useSuccessToast(TOAST.success, t("common.moved-to-review-successfully"));
        } else if (newStatus === NOTICE_STATUS.ON_WRITE) {
          useSuccessToast(TOAST.success, t("common.rejected-review-successfully"));
        } else if (newStatus === NOTICE_STATUS.DONE) {
          useSuccessToast(TOAST.success, t("common.moved-to-done-successfully"));
        } else {
          useSuccessToast(TOAST.success, t("common.status-updated-successfully"));
        }
        
        setData((prev) => ({
          ...prev,
          noticeStatus: response.noticeInfoStatusCd,
          noticeInfoStatusCdName: response.noticeInfoStatusCdName,
          noticeInfoWriteStatusCd: response.noticeInfoWriteStatusCd,
        }));
        refetchNoticeInfoComponents();
        refetchNoticeInfoData();
      },
      onError: (error) => {
      },
    },
  );

  const noticeInfoDownloadByText = useMutationData(
    queryKeys.noticeInfoDownloadText,
    {
      method: "GET",
      params: { noticeInfoSeq: noticeInfoSeq },
      responseType: "blob",
    },
    {
      onSuccess: (response) => {
        const url = window.URL.createObjectURL(new Blob([response]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `NOTICE_INFO_${data?.noticeInfo?.projectId || ""}_${data?.noticeInfo?.noticeInfoId || ""}_${noticeInfoSeq}.txt`,
        );
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      },
      onError: (error) => {
      },
    },
  );

  const noticeInfoDownloadByHtml = useMutationData(
    queryKeys.noticeInfoDownloadHtml,
    {
      method: "GET",
      params: { noticeInfoSeq: noticeInfoSeq },
      responseType: "blob",
    },
    {
      onSuccess: (response) => {
        const url = window.URL.createObjectURL(new Blob([response]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `NOTICE_INFO_${data?.noticeInfo?.projectId || ""}_${data?.noticeInfo?.noticeInfoId || ""}_${noticeInfoSeq}.html`,
        );
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      },
      onError: (error) => {
      },
    },
  );


  // Handler
  const removeDuplicates = (items) => {
    const seen = new Set();
    return items.filter((item) => {
      const key = `${item.componentName}-${item.componentVersion}`;
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
  };
  // Function move steps
  const handleClose = () => {
    navigate(-1);
  };
  const handleMoveToReview = () => {
    const payload = {
      noticeInfoSeq: noticeInfoSeq,
      noticeInfoStatusCd: NOTICE_STATUS.ON_REVIEW,
    };
    updateNoticeInfoStatus.mutate(payload);
  };
  const handleRejectReview = () => {
    const payload = {
      noticeInfoSeq: noticeInfoSeq,
      noticeInfoStatusCd: NOTICE_STATUS.REQUEST,
    };
    updateNoticeInfoStatus.mutate(payload);
  };
  const handleMoveToDone = () => {
    const payload = {
      noticeInfoSeq: noticeInfoSeq,
      noticeInfoStatusCd: NOTICE_STATUS.DONE,
    };
    updateNoticeInfoStatus.mutate(payload);
  };

  // Hooks
  useEffect(() => {
    if (fetchedNoticeInfoData) {
      setData((prev) => ({
        ...prev,
        noticeInfo: fetchedNoticeInfoData,
        noticeStatus: fetchedNoticeInfoData?.noticeInfoStatusCd || NOTICE_STATUS.REQUEST,
        noticeInfoStatusCdName: fetchedNoticeInfoData?.noticeInfoStatusCdName || "Writing...",
        noticeInfoWriteStatusCd: fetchedNoticeInfoData?.noticeInfoWriteStatusCd || NOTICE_STATUS.REQUEST,
      }));
    }
    if (fetchedNoticeInfoComponents) {
      const uniqueComponents = removeDuplicates(fetchedNoticeInfoComponents);
      setData((prev) => ({
        ...prev,
        noticeInfoComponents: uniqueComponents || [],
      }));
    }
  }, [fetchedNoticeInfoData, fetchedNoticeInfoComponents]);

  useEffect(() => {
    if (fetchedProjectByProjectID) {
      setData((prev) => ({
        ...prev,
        projectData: fetchedProjectByProjectID,
      }));
    }
  }, [fetchedProjectByProjectID]);

  useEffect(() => {
    if (fetchedVerificationData && fetchedLicensePolicy) {
      setData((prev) => ({
        ...prev,
        verificationReportData: fetchedVerificationData,
        scanComponents: fetchedVerificationData?.verificationReportDetails || [],
        licensePolicyData: fetchedLicensePolicy,
      }));
    }
  }, [fetchedVerificationData, fetchedLicensePolicy]);

  return {
    noticeStatus: data.noticeStatus,
    noticeInfo: data.noticeInfo,
    projectData: data.projectData,
    verificationReportData: data.verificationReportData,
    scanComponents: data.scanComponents,
    licensePolicyData: data.licensePolicyData,
    noticeInfoDownloadByText,
    noticeInfoDownloadByHtml,
    updateNoticeInfoNoticeInfoWriteStatus,
    noticeInfoComponents: data.noticeInfoComponents,
    noticeInfoStatusCdName: data.noticeInfoStatusCdName,
    noticeInfoWriteStatusCd: data.noticeInfoWriteStatusCd,
    handleClose,
    handleMoveToReview,
    handleRejectReview,
    handleMoveToDone,
    isLoadingProject,
    isLoadingNoticeInfo,
    isLoadingVerification,
    isLoading: isLoadingNoticeInfo || isLoadingProject || isLoadingVerification || isLoadingLicensePolicy,
    error: noticeInfoError || projectError || verificationError || licensePolicyError,
  };
};
